import { useState } from "react";
import { trpc } from "@/utils/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, Zap } from "lucide-react";
import { toast } from "sonner";

export default function EscalationSettings() {
  const { data: queues = [] } = trpc.tickets.ticketQueues.list.useQuery();
  const { data: users = [] } = trpc.users.list.useQuery();
  const { data: roles = [] } = trpc.roles.list.useQuery();
  const [selectedQueueId, setSelectedQueueId] = useState<string>("");

  const { data: rules = [], isLoading, refetch } = trpc.tickets.escalation.getRules.useQuery(
    { queueId: Number(selectedQueueId) },
    { enabled: !!selectedQueueId },
  );

  const createMut = trpc.tickets.escalation.create.useMutation({ onSuccess: () => { refetch(); toast.success("Rule created"); } });
  const deleteMut = trpc.tickets.escalation.delete.useMutation({ onSuccess: () => { refetch(); toast.success("Rule deleted"); } });

  const [newRule, setNewRule] = useState({
    priorityLevel: "p1" as string,
    triggerPercent: 80,
    action: "notify_assignee" as string,
    assignToUserId: "" as string,
    assignToRoleId: "" as string,
    autoBumpPriority: false
  });

  const handleAdd = () => {
    if (!selectedQueueId) return;
    const payload: any = { queueId: Number(selectedQueueId), ...newRule };
    if (newRule.assignToUserId) payload.notifyUserId = Number(newRule.assignToUserId);
    if (newRule.assignToRoleId) payload.notifyRoleId = Number(newRule.assignToRoleId);
    createMut.mutate(payload);
  };

  const ACTION_LABELS: Record<string, string> = {
    notify_assignee: "Notify Assignee",
    assign_to_user: "Assign to Specific Person",
    assign_to_role: "Assign to Specific Role",
    auto_reassign: "Auto-Reassign",
  };

  const PRIORITY_COLORS: Record<string, string> = {
    p1: "bg-[#FFE5E5] text-[#DC2626] border-[#FFE5E5]",
    p2: "bg-[#FFEDD5] text-[#EA580C] border-[#FFEDD5]",
    p3: "bg-[#FFF4E5] text-[#D97706] border-[#FFF4E5]",
    p4: "bg-[#E8F9F8] text-[#0D9488] border-[#E8F9F8]",
  };

  return (
    <div className="px-0 py-5 space-y-5">
      {/* Queue selector */}
      <div className="flex items-center gap-4">
        <div className="w-64">
          <label className="text-sm font-medium text-[#2C2C2C] block mb-1">Select Queue</label>
          <Select value={selectedQueueId} onValueChange={setSelectedQueueId}>
            <SelectTrigger className="border-[#E0E0E0]"><SelectValue placeholder="Choose a queue" /></SelectTrigger>
            <SelectContent>{queues.map((q: any) => <SelectItem key={q.id} value={String(q.id)}>{q.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {!selectedQueueId && (
        <div className="rounded-lg border border-[#E0E0E0] py-16 text-center">
          <Zap className="h-10 w-10 mx-auto text-[#B0B0B0] mb-3" />
          <p className="text-sm text-[#6B6B6B]">Select a queue above to view and manage escalation rules</p>
        </div>
      )}

      {selectedQueueId && (
        <>
          {/* Existing rules */}
          <div className="rounded-lg border border-[#E0E0E0] overflow-hidden">
            <div className="px-6 py-3.5 border-b border-[#E0E0E0] bg-[#FAFAFA]">
              <h3 className="text-sm font-medium text-[#2C2C2C] uppercase tracking-wide">Escalation Rules</h3>
            </div>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-[#E0E0E0] bg-[#FAFAFA]">
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Priority</th>
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Trigger %</th>
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Action</th>
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Assigned To</th>
                <th className="text-left px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Auto-Bump</th>
                <th className="text-right px-6 py-3 text-[11px] font-medium text-[#6B6B6B] uppercase tracking-wider">Delete</th>
              </tr></thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={5} className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#5B2C93]" /></td></tr>
                ) : rules.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-[#9CA3AF] text-sm">No escalation rules for this queue</td></tr>
                ) : (
                  rules.map((r: any) => {
                    const assignedUser = r.notifyUserId ? users.find((u: any) => u.id === r.notifyUserId) : null;
                    const assignedRole = r.notifyRoleId ? roles.find((ro: any) => ro.id === r.notifyRoleId) : null;
                    return (
                      <tr key={r.id} className="border-b border-[#F0F0F0] last:border-0 hover:bg-[#F5F8FF]">
                        <td className="px-6 py-3"><Badge variant="outline" className={PRIORITY_COLORS[r.priorityLevel] ?? ""}>{r.priorityLevel?.toUpperCase()}</Badge></td>
                        <td className="px-6 py-3 font-medium text-[#2C2C2C]">{r.triggerPercent}%</td>
                        <td className="px-6 py-3 text-[#6B6B6B]">{ACTION_LABELS[r.action] ?? r.action}</td>
                        <td className="px-6 py-3 text-[#6B6B6B]">
                          {assignedUser ? <Badge variant="outline" className="bg-[#E8DCF5] text-[#5B2C93] border-[#E8DCF5]">{assignedUser.name}</Badge> :
                           assignedRole ? <Badge variant="outline" className="bg-[#E8DCF5] text-[#5B2C93] border-[#E8DCF5]">{assignedRole.name}</Badge> :
                           <span className="text-[#9CA3AF]">—</span>}
                        </td>
                        <td className="px-6 py-3">{r.autoBumpPriority ? <Badge variant="outline" className="bg-[#FFF4E5] text-[#D97706] border-[#FFF4E5]">Yes</Badge> : <span className="text-[#9CA3AF]">No</span>}</td>
                        <td className="px-6 py-3 text-right"><Button size="sm" variant="outline" className="text-[#DC2626] border-[#E0E0E0] hover:bg-[#FFE5E5]" onClick={() => deleteMut.mutate({ id: r.id })}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Add new rule */}
          <div className="rounded-lg border border-[#E0E0E0] overflow-hidden">
            <div className="px-6 py-3.5 border-b border-[#E0E0E0] bg-[#FAFAFA]">
              <h3 className="text-sm font-medium text-[#2C2C2C] uppercase tracking-wide flex items-center gap-2"><Zap className="h-4 w-4 text-[#5B2C93]" />Add Escalation Rule</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex gap-3 items-end flex-wrap">
                <div className="w-32"><label className="text-xs font-medium text-[#6B6B6B]">Priority</label>
                  <Select value={newRule.priorityLevel} onValueChange={v => setNewRule(r => ({ ...r, priorityLevel: v }))}><SelectTrigger className="mt-1 border-[#E0E0E0]"><SelectValue /></SelectTrigger><SelectContent>{["p1","p2","p3","p4"].map(p => <SelectItem key={p} value={p}>{p.toUpperCase()}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="w-28"><label className="text-xs font-medium text-[#6B6B6B]">Trigger %</label><Input type="number" value={newRule.triggerPercent} onChange={e => setNewRule(r => ({ ...r, triggerPercent: Number(e.target.value) }))} className="mt-1 border-[#E0E0E0]" /></div>
                <div className="w-56"><label className="text-xs font-medium text-[#6B6B6B]">Action</label>
                  <Select value={newRule.action} onValueChange={v => setNewRule(r => ({ ...r, action: v, assignToUserId: "", assignToRoleId: "" }))}><SelectTrigger className="mt-1 border-[#E0E0E0]"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(ACTION_LABELS).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                </div>
                <label className="flex items-center gap-1.5 text-xs pb-2 text-[#6B6B6B]"><input type="checkbox" checked={newRule.autoBumpPriority} onChange={e => setNewRule(r => ({ ...r, autoBumpPriority: e.target.checked }))} className="rounded" />Auto-bump</label>
                <Button onClick={handleAdd} disabled={createMut.isPending} size="sm" className="bg-[#5B2C93] hover:bg-[#4A2377] text-white gap-1.5"><Plus className="h-4 w-4" />Add</Button>
              </div>

              {/* Conditional selectors for assign_to_user or assign_to_role */}
              {newRule.action === "assign_to_user" && (
                <div className="w-56"><label className="text-xs font-medium text-[#6B6B6B]">Select Person</label>
                  <Select value={newRule.assignToUserId} onValueChange={v => setNewRule(r => ({ ...r, assignToUserId: v }))}>
                    <SelectTrigger className="mt-1 border-[#E0E0E0]"><SelectValue placeholder="Choose a person" /></SelectTrigger>
                    <SelectContent>{users.map((u: any) => <SelectItem key={u.id} value={String(u.id)}>{u.name} ({u.email})</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              {newRule.action === "assign_to_role" && (
                <div className="w-56"><label className="text-xs font-medium text-[#6B6B6B]">Select Role</label>
                  <Select value={newRule.assignToRoleId} onValueChange={v => setNewRule(r => ({ ...r, assignToRoleId: v }))}>
                    <SelectTrigger className="mt-1 border-[#E0E0E0]"><SelectValue placeholder="Choose a role" /></SelectTrigger>
                    <SelectContent>{roles.map((ro: any) => <SelectItem key={ro.id} value={String(ro.id)}>{ro.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
